import { useState } from "react";
import { createPortal } from "react-dom";
import {
  Building2,
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Users,
  User,
  Coffee,
  Shirt,
  CheckCircle,
  XCircle,
  Clock as ClockIcon,
  AlertCircle,
  Zap,
  Briefcase,
  Eye,
  Send,
  X,
  Check,
  HelpCircle,
  Filter,
  Award,
  Star,
  TrendingUp,
  Loader2,
  ExternalLink,
  Gift,
  Info,
  Ban,
} from "lucide-react";
import { applyToJob, cancelApplication } from "../../../../services/applicationService";
import { formatDatePtBr } from "../../../../utils/dateUtils";
import Toast from "../../../../Components/Toast/Toast"
import "./JobCard.scss";

function formatTime(t) {
  return t ? t.slice(0, 5) : "";
}

function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  return fallback;
}

// ── Modal de perguntas de filtro ──────────────────────────────────────────
// Não decide mais localmente se o freela passou ou não no filtro — só coleta
// as respostas e devolve pro backend, que é quem valida contra o gabarito
// (JobQuestion.ExpectedAnswer) e registra a reprovação de forma persistente.
function QuestionsModal({ questions, onConfirm, onClose }) {
  const [answers, setAnswers] = useState(
    Object.fromEntries(questions.map((q) => [q.id, null]))
  );

  const allAnswered = questions.every((q) => answers[q.id] !== null);

  const handleConfirm = () => {
    const payload = questions.map((q) => ({
      questionId: q.id,
      answer: answers[q.id],
    }));
    onConfirm(payload);
  };

  return (
    <div className="job-card-questions-overlay" onClick={onClose}>
      <div className="job-card-questions-modal" onClick={(e) => e.stopPropagation()}>
        <div className="job-card-questions-modal__header">
          <div className="job-card-questions-modal__title">
            <Filter size={20} />
            <h3>Filtro da Vaga</h3>
          </div>
          <button onClick={onClose} className="job-card-questions-modal__close">
            <X size={20} />
          </button>
        </div>

        <p className="job-card-questions-modal__desc">
          <HelpCircle size={16} />
          Responda as perguntas abaixo para continuar com a candidatura.
        </p>

        <div className="job-card-questions-modal__list">
          {questions.map((q, idx) => (
            <div key={q.id} className="job-card-question">
              <p className="job-card-question__text">
                <span className="job-card-question__number">{idx + 1}.</span>
                {q.question}
              </p>
              <div className="job-card-question__btns">
                <button
                  className={`job-card-question__btn job-card-question__btn--yes ${answers[q.id] === "sim" ? "active" : ""
                    }`}
                  onClick={() => setAnswers((a) => ({ ...a, [q.id]: "sim" }))}
                >
                  <Check size={16} />
                  Sim
                </button>
                <button
                  className={`job-card-question__btn job-card-question__btn--no ${answers[q.id] === "nao" ? "active" : ""
                    }`}
                  onClick={() => setAnswers((a) => ({ ...a, [q.id]: "nao" }))}
                >
                  <X size={16} />
                  Não
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="job-card-questions-modal__footer">
          <button className="job-card-questions-modal__cancel" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="job-card-questions-modal__confirm"
            disabled={!allAnswered}
            onClick={handleConfirm}
          >
            <Send size={16} />
            Confirmar candidatura
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal de bônus (em portal — não pode ser filho do card) ───────────────
function BonusModal({ job, onClose }) {
  return createPortal(
    <div className="jobcard-bonus-modal">
      <div className="jobcard-bonus-modal__overlay" onClick={onClose} />

      <div className="jobcard-bonus-modal__container" onClick={(e) => e.stopPropagation()}>
        <div className="jobcard-bonus-modal__header">
          <h4 className="jobcard-bonus-modal__title">
            <Gift size={20} />
            Bônus da vaga
          </h4>
          <button
            className="jobcard-bonus-modal__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="jobcard-bonus-modal__content">
          {job.bonusDescription}
        </div>

        <div className="jobcard-bonus-modal__actions">
          <button className="jobcard-bonus-modal__btn" onClick={onClose}>
            <X size={16} />
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── JobCard ───────────────────────────────────────────────────────────────
export default function JobCard({
  job,
  applicationStatus: initialStatus = null,
  onViewFinished,
  onViewCompany,
  viewMode = "grid"
}) {
  const [status, setStatus] = useState(initialStatus);
  const [loading, setLoading] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [showBonus, setShowBonus] = useState(false);

  const hasQuestions = job.questions && job.questions.length > 0;

  const [toast, setToast] = useState({ message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  // answers: array de { questionId, answer } coletado no QuestionsModal
  // (undefined quando a vaga não tem perguntas de filtro).
  const doApply = async (answers) => {
    setLoading(true);
    try {
      await applyToJob(job.id, answers);
      setStatus("pending");
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.error === "filter_rejected") {
        // Reprovado no filtro: o backend já registrou isso de forma
        // persistente, então travamos o botão pra ele não tentar de novo.
        setStatus("filter_rejected");
        showToast(
          err.response.data.message ||
          "Você não atende aos requisitos desta vaga e não pode se candidatar.",
          "error"
        );
      } else {
        const msg = getErrorMessage(err, "Erro ao se candidatar.");
        showToast(msg, "error");
      }
    } finally {
      setLoading(false);
      setShowQuestions(false);
    }
  };

  const handleApplyClick = () => {
    if (hasQuestions) {
      setShowQuestions(true);
    } else {
      doApply();
    }
  };

  const handleCancel = async () => {
    if (!confirm("Deseja cancelar sua candidatura?")) return;
    setLoading(true);
    try {
      await cancelApplication(job.id);
      setStatus(null);
    } catch (err) {
      showToast(err?.response?.data || "Erro ao cancelar candidatura.", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderFooterBtn = () => {
    switch (status) {
      case "completed":
      case "early_exit":
      case "expired":
        return (
          <button
            className="job-card__apply-btn job-card__apply-btn--finished"
            onClick={onViewFinished}
          >
            <Eye size={16} />
            Ver vaga finalizada
          </button>
        );

      case "approved":
        return (
          <span className="job-card__status job-card__status--approved">
            <CheckCircle size={16} />
            Aprovado
          </span>
        );

      case "rejected":
        return (
          <span className="job-card__status job-card__status--rejected">
            <XCircle size={16} />
            Recusado
          </span>
        );

      case "filter_rejected":
        return (
          <span className="job-card__status job-card__status--blocked">
            <Ban size={16} />
            Não elegível para esta vaga
          </span>
        );

      case "pending":
        return (
          <button
            className="job-card__apply-btn job-card__apply-btn--pending"
            onClick={handleCancel}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="job-card__spinner" />
                Cancelando...
              </>
            ) : (
              <>
                <ClockIcon size={16} />
                Cancelar candidatura
              </>
            )}
          </button>
        );

      default:
        return (
          <button
            className="job-card__apply-btn"
            onClick={handleApplyClick}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="job-card__spinner" />
                Enviando...
              </>
            ) : (
              <>
                <Send size={16} />
                Candidatar-se
              </>
            )}
          </button>
        );
    }
  };

  return (
    <>
      <div className={`job-card ${job.isUrgent ? "job-card--urgent" : ""} ${viewMode === "list" ? "job-card--list" : ""}`}>
        {/* Urgent Banner */}
        {job.isUrgent && (
          <div className="job-card__urgent-banner">
            <Zap size={14} />
            Vaga urgente
          </div>
        )}

        {job.bonusDescription && (
          <div className="jobcard-bonus">
            <span className="jobcard-bonus__badge">
              <Gift size={14} />
              Bônus disponível
            </span>

            <button
              className="jobcard-bonus__info"
              onClick={(e) => {
                e.stopPropagation();
                setShowBonus(true);
              }}
              aria-label="Ver detalhes do bônus"
            >
              <Info size={16} />
            </button>
          </div>
        )}

        <div className="job-card__header">
          <div className="job-card__company-row">
            {job.companyAvatar ? (
              <img src={job.companyAvatar} alt={job.companyName} className="job-card__avatar" />
            ) : (
              <div className="job-card__avatar-fallback">
                {job.companyName?.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="job-card__company-info">
              <p className="job-card__title">{job.title}</p>
              <div className="job-card__company-details">
                <Building2 size={14} className="job-card__company-icon" />
                <p className="job-card__company-name">{job.companyName}</p>
                <button className="job-card__company-link" onClick={onViewCompany}>
                  <ExternalLink size={12} />
                  Ver empresa
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Meta Info */}
        <div className="job-card__meta">
          <span className="job-card__meta-item">
            <Calendar size={14} />
            {formatDatePtBr(job.date)}
          </span>
          <span className="job-card__meta-item">
            <Clock size={14} />
            {formatTime(job.startTime)} – {formatTime(job.endTime)}
          </span>
          <span className="job-card__meta-item">
            <MapPin size={14} />
            {job.location}
          </span>
        </div>

        {/* Description */}
        {job.description && (
          <p className="job-card__description">{job.description}</p>
        )}

        {/* Tags */}
        <div className="job-card__tags">
          {job.dressCode && (
            <span className="job-card__tag">
              <Shirt size={12} />
              {job.dressCode}
            </span>
          )}
          {job.breakTime && (
            <span className="job-card__tag">
              <Coffee size={12} />
              Intervalo: {job.breakTime} min
            </span>
          )}
          {job.contactPerson && (
            <span className="job-card__tag">
              <User size={12} />
              {job.contactPerson}
            </span>
          )}
          {hasQuestions && (
            <span className="job-card__tag job-card__tag--filter">
              <Filter size={12} />
              {job.questions.length} pergunta{job.questions.length > 1 ? "s" : ""} de filtro
            </span>
          )}
          {job.isUrgent && (
            <span className="job-card__tag job-card__tag--urgent">
              <Zap size={12} />
              Urgente
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="job-card__footer">
          <div className="job-card__payment-wrapper">
            <DollarSign size={18} className="job-card__payment-icon" />
            <span className="job-card__payment">
              R$ {Number(job.payment).toFixed(2)}
            </span>
          </div>
          {renderFooterBtn()}
        </div>
      </div>

      {showQuestions && (
        <QuestionsModal
          questions={job.questions}
          onConfirm={doApply}
          onClose={() => setShowQuestions(false)}
        />
      )}

      {showBonus && (
        <BonusModal job={job} onClose={() => setShowBonus(false)} />
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "success" })}
      />
    </>
  );
}